import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  Container,
  Badge,
  useColorModeValue,
  keyframes,
  CircularProgress,
  CircularProgressLabel,
  Divider,
  Grid,
  GridItem,
  chakra,
  Button,
  useDisclosure,
  Collapse,
  ScaleFade,
  Image,
} from "@chakra-ui/react";
import {
  FaGraduationCap,
  FaLaptopCode,
  FaBriefcase,
  FaUsers,
  FaChartLine,
} from "react-icons/fa";

// Animație pentru elementele futuristice
const glowAnimation = keyframes`
  0% { box-shadow: 0 0 5px #3182ce; }
  50% { box-shadow: 0 0 20px #3182ce, 0 0 30px #63b3ed; }
  100% { box-shadow: 0 0 5px #3182ce; }
`;

// Animație pentru liniile de conectare
const pulseAnimation = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 0.3; }
`;

const CREATEComponent = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const { isOpen, onToggle } = useDisclosure();

  // Culori pentru tema futuristică
  const bgColor = useColorModeValue("white", "white");
  const textColor = useColorModeValue("gray.700", "gray.700");
  const accentColor = useColorModeValue("cyan.500", "cyan.500");
  const cardBg = useColorModeValue("white", "white");
  const glowColor = useColorModeValue("blue.400", "blue.400");

  // Animație pentru titlu
  const [titleChars, setTitleChars] = useState([]);
  const title = "C.R.E.A.T.E";

  useEffect(() => {
    const chars = title.split("");
    let timeouts = [];

    for (let i = 0; i < chars.length; i++) {
      timeouts.push(
        setTimeout(() => {
          setTitleChars((prev) => [...prev, chars[i]]);
        }, i * 200)
      );
    }

    setTimeout(() => {
      setShowDetails(true);
    }, title.length * 200 + 500);

    return () => timeouts.forEach((timeout) => clearTimeout(timeout));
  }, []);

  // Stiluri pentru animația glow
  const glowStyle = {
    animation: `${glowAnimation} 3s infinite`,
  };

  const pulseStyle = {
    animation: `${pulseAnimation} 2s infinite`,
  };

  // Statistici proiect
  const stats = [
    { label: "Elevi", value: 252, icon: FaUsers, color: "blue.400" },
    { label: "Luni", value: 36, icon: FaChartLine, color: "green.400" },
    {
      label: "Regiune",
      value: "Sud-Muntenia",
      icon: FaLaptopCode,
      color: "purple.400",
    },
  ];

  // Etapele procesului
  const stages = [
    {
      name: "Școală",
      icon: FaGraduationCap,
      color: "blue.400",
      description: "Pregătirea teoretică și dezvoltarea competențelor de bază",
    },
    {
      name: "Stagiu",
      icon: FaLaptopCode,
      color: "green.400",
      description:
        "Aplicarea cunoștințelor în medii reale de practică profesională",
    },
    {
      name: "Loc de muncă",
      icon: FaBriefcase,
      color: "purple.400",
      description:
        "Tranziția către piața muncii și începerea carierei profesionale",
    },
  ];

  return (
    <Box
      p={6}
      bg={bgColor}
      color={textColor}
      borderRadius="xl"
      overflow="hidden"
      position="relative"
      minH="100vh"
      sx={{
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(49, 130, 206, 0.05) 0%, transparent 40%), " +
            "radial-gradient(circle at 80% 70%, rgba(76, 81, 191, 0.03) 0%, transparent 40%)",
          zIndex: 0,
        },
      }}
    >
      {/* Grid futuristic în fundal */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        opacity={0.05}
        zIndex={0}
        sx={{
          backgroundImage:
            "linear-gradient(rgba(49, 130, 206, 0.2) 1px, transparent 1px), " +
            "linear-gradient(90deg, rgba(49, 130, 206, 0.2) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      {/* Logos Section */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        pt={8}
        pb={4}
        position="relative"
        zIndex={1}
      >
        <HStack
          spacing={{ base: 8, md: 16, lg: 20 }}
          mb={8}
          justify="center"
          align="center"
          flexWrap="wrap"
        >
          {/* Logo 1 - EU Logo */}
          <Box
            w={{ base: "120px", md: "150px", lg: "180px" }}
            h={{ base: "80px", md: "100px", lg: "120px" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="white"
            borderRadius="lg"
            border="2px solid"
            borderColor="blue.200"
            p={3}
            _hover={{ transform: "scale(1.05)" }}
            transition="all 0.3s"
            boxShadow="md"
            overflow="hidden"
          >
            <Image
              src="https://www.travel-time.ro/images/ue%20logo.png"
              alt="European Union Logo"
              maxH="100%"
              maxW="100%"
              objectFit="contain"
              fallback={
                <VStack spacing={1}>
                  <Box
                    w="60px"
                    h="40px"
                    bg="blue.600"
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color="yellow.300"
                    fontSize="xs"
                    fontWeight="bold"
                    textAlign="center"
                  >
                    EU
                  </Box>
                  <Text
                    fontSize="xs"
                    color="blue.600"
                    fontWeight="medium"
                    textAlign="center"
                  >
                    European Union
                  </Text>
                </VStack>
              }
            />
          </Box>

          {/* Logo 2 - Romania Logo */}
          <Box
            w={{ base: "120px", md: "150px", lg: "180px" }}
            h={{ base: "80px", md: "100px", lg: "120px" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="white"
            borderRadius="lg"
            border="2px solid"
            borderColor="gray.200"
            p={3}
            _hover={{ transform: "scale(1.05)" }}
            transition="all 0.3s"
            boxShadow="md"
            overflow="hidden"
          >
            <Image
              src="https://www.travel-time.ro/images/guvern%20logo.png"
              alt="Romania Logo"
              maxH="100%"
              maxW="100%"
              objectFit="contain"
              fallback={
                <VStack spacing={1}>
                  <HStack spacing={0}>
                    <Box w="15px" h="25px" bg="blue.600" />
                    <Box w="15px" h="25px" bg="yellow.400" />
                    <Box w="15px" h="25px" bg="red.600" />
                  </HStack>
                  <Text
                    fontSize="xs"
                    color="red.600"
                    fontWeight="medium"
                    textAlign="center"
                  >
                    România
                  </Text>
                </VStack>
              }
            />
          </Box>
        </HStack>

        {/* Separator line */}
        <Box
          w={{ base: "200px", md: "300px" }}
          h="2px"
          bg="linear-gradient(90deg, transparent, #4299E1, #48BB78, #9F7AEA, transparent)"
          mb={8}
          style={pulseStyle}
        />
      </Flex>

      {/* Titlu animat */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        py={12}
        position="relative"
        zIndex={1}
      >
        <HStack spacing={1} mb={6}>
          {titleChars.map((char, index) => (
            <chakra.span
              key={index}
              fontSize={{ base: "4xl", md: "6xl" }}
              fontWeight="bold"
              color={index % 2 === 0 ? accentColor : "blue.400"}
              textShadow={`0 0 15px ${glowColor}`}
              style={glowStyle}
              transition="all 0.5s"
            >
              {char}
            </chakra.span>
          ))}
        </HStack>

        <ScaleFade in={showDetails} initialScale={0.9}>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            textAlign="center"
            fontWeight="medium"
            maxW="800px"
            mb={4}
            bgGradient="linear(to-r, blue.500, cyan.500)"
            bgClip="text"
          >
            Competențe și Resurse pentru Educație și Adaptare Tehnologică ale
            Elevilor - 312355
          </Text>
        </ScaleFade>
      </Flex>

      {/* Conținut principal */}
      <Container maxW="6xl" position="relative" zIndex={1}>
        {/* Obiectiv general */}
        <ScaleFade in={showDetails} initialScale={0.9}>
          <Box
            mb={12}
            p={6}
            borderRadius="lg"
            bg={cardBg}
            boxShadow="xl"
            position="relative"
            overflow="hidden"
            sx={glowStyle}
          >
            <Heading
              as="h2"
              size="lg"
              mb={4}
              textAlign="center"
              bgGradient="linear(to-r, cyan.400, blue.400)"
              bgClip="text"
            >
              Obiectivul General al Proiectului
            </Heading>
            <Text fontSize="lg" lineHeight="tall" textAlign="center">
              Obiectivul general al proiectului este „Îmbunătățirea
              competențelor profesionale pentru un număr de 252 de elevi
              înmatriculați în învățământul profesional și tehnic din regiunea
              Sud-Muntenia, în vederea facilitării tranziției de la școală la
              viața activă, într-un interval de 36 de luni.”
            </Text>
            <Text fontSize="lg" mt={4} lineHeight="tall" textAlign="center">
              Proiectul vizează sprijinirea elevilor prin consolidarea
              parteneriatelor și o strânsă colaborare între unitatea de
              învățământ și mediul privat, asigurând relevanța programelor de
              studii pentru nevoile actuale ale pieței muncii.
            </Text>
            <Text fontSize="lg" mt={4} lineHeight="tall" textAlign="center">
              Proiectul este cofinanțat prin Fondul Social European+, prin
              Programul Educație și Ocupare, Prioritate P8. Creșterea
              accesibilității, atractivității și calității învățământului
              profesional și tehnic, Obiectiv specific ESO4.5_Îmbunătățirea
              calității, a caracterului incluziv, a eficacității și a relevanței
              sistemelor de educație și formare pentru piața muncii, inclusiv
              prin validarea învățării non-formale și informale,pentru a
              sprijini dobândirea de competențe-cheie, inclusiv de competențe de
              antreprenoriat și digitale, precum și prin promovarea introducerii
              sistemelor de formare duală și a sistemelor de ucenicie Fondul
              Social European+.
            </Text>
            {/* Statistici */}
            <Grid
              templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }}
              gap={6}
              mt={8}
            >
              {stats.map((stat, index) => (
                <GridItem key={index}>
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    p={4}
                    borderRadius="md"
                    bg="gray.700"
                    boxShadow="md"
                  >
                    <Circle color={stat.color} icon={stat.icon} />
                    <Text
                      fontSize="4xl"
                      fontWeight="bold"
                      mt={2}
                      color={stat.color}
                    >
                      {stat.value}
                    </Text>
                    <Text fontSize="md" color="gray.300">
                      {stat.label}
                    </Text>
                  </Flex>
                </GridItem>
              ))}
            </Grid>
          </Box>
        </ScaleFade>

        {/* Etapele procesului */}
        <ScaleFade in={showDetails} initialScale={0.9} delay={0.3}>
          <Box mb={12}>
            <Heading
              as="h2"
              size="lg"
              mb={6}
              textAlign="center"
              bgGradient="linear(to-r, cyan.400, blue.400)"
              bgClip="text"
            >
              Procesul Educațional
            </Heading>

            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align="center"
              position="relative"
            >
              {/* Linie de conectare între etape */}
              <Box
                display={{ base: "none", md: "block" }}
                position="absolute"
                height="4px"
                width="80%"
                left="10%"
                top="50%"
                bg="gray.700"
                zIndex={0}
              >
                <Box
                  height="100%"
                  width="100%"
                  bg="linear-gradient(90deg, #4299E1, #48BB78, #9F7AEA)"
                  borderRadius="full"
                  style={pulseStyle}
                />
              </Box>

              {/* Etapele */}
              {stages.map((stage, index) => (
                <VStack
                  key={index}
                  spacing={4}
                  w={{ base: "full", md: "30%" }}
                  position="relative"
                  zIndex={1}
                  mb={{ base: 8, md: 0 }}
                  onMouseEnter={() => setActiveSection(index)}
                  onMouseLeave={() => setActiveSection(null)}
                >
                  <Box
                    p={6}
                    borderRadius="full"
                    bg={cardBg}
                    boxShadow="lg"
                    _hover={{ transform: "scale(1.05)" }}
                    transition="all 0.3s"
                    style={activeSection === index ? glowStyle : {}}
                    border="2px solid"
                    borderColor={stage.color}
                  >
                    <Icon as={stage.icon} w={10} h={10} color={stage.color} />
                  </Box>
                  <Heading size="md" color={stage.color}>
                    {stage.name}
                  </Heading>
                  <Collapse
                    in={activeSection === index || activeSection === null}
                  >
                    <Text textAlign="center" color="gray.800" fontSize="sm">
                      {stage.description}
                    </Text>
                  </Collapse>
                </VStack>
              ))}
            </Flex>
          </Box>
        </ScaleFade>

        {/* Obiective detaliate */}
        <ScaleFade in={showDetails} initialScale={0.9} delay={0.3}>
          <Box mb={10}>
            <Heading
              as="h2"
              size="lg"
              mb={6}
              textAlign="center"
              bgGradient="linear(to-r, cyan.500, blue.500)"
              bgClip="text"
            >
              Obiective Specifice
            </Heading>

            <Grid
              templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }}
              gap={8}
            >
              {/* Obiectiv 1 */}
              <GridItem colSpan={1}>
                <Box
                  p={6}
                  borderRadius="lg"
                  bg={cardBg}
                  boxShadow="xl"
                  border="1px solid"
                  borderColor="blue.100"
                  h="100%"
                  transition="all 0.3s"
                  _hover={{ transform: "translateY(-5px)", boxShadow: "2xl" }}
                >
                  <Heading
                    as="h3"
                    size="md"
                    mb={4}
                    color="blue.500"
                    display="flex"
                    alignItems="center"
                  >
                    <Box
                      w="30px"
                      h="30px"
                      borderRadius="full"
                      bg="blue.500"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mr={3}
                      fontSize="sm"
                      fontWeight="bold"
                    >
                      1
                    </Box>
                    Facilitarea tranziției elevilor de la educație la piața
                    muncii
                  </Heading>
                  <VStack align="start" spacing={3}>
                    <HStack align="start" spacing={3}>
                      <Box
                        w="2px"
                        h="100%"
                        bg="blue.100"
                        borderRadius="full"
                        mt={1}
                      />
                      <Text>
                        <Text as="span" fontWeight="bold">
                          Stagii de practică
                        </Text>{" "}
                        în companii din diverse domenii.
                      </Text>
                    </HStack>
                    <HStack align="start" spacing={3}>
                      <Box
                        w="2px"
                        h="100%"
                        bg="blue.100"
                        borderRadius="full"
                        mt={1}
                      />
                      <Text>
                        Implicarea angajatorilor în{" "}
                        <Text as="span" fontWeight="bold">
                          programe de învățare la locul de muncă
                        </Text>
                        .
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              {/* Obiectiv 2 */}
              <GridItem colSpan={1}>
                <Box
                  p={6}
                  borderRadius="lg"
                  bg={cardBg}
                  boxShadow="xl"
                  border="1px solid"
                  borderColor="cyan.100"
                  h="100%"
                  transition="all 0.3s"
                  _hover={{ transform: "translateY(-5px)", boxShadow: "2xl" }}
                >
                  <Heading
                    as="h3"
                    size="md"
                    mb={4}
                    color="cyan.500"
                    display="flex"
                    alignItems="center"
                  >
                    <Box
                      w="30px"
                      h="30px"
                      borderRadius="full"
                      bg="cyan.500"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mr={3}
                      fontSize="sm"
                      fontWeight="bold"
                    >
                      2
                    </Box>
                    Colaborarea dintre unitățile de învățământ și mediul privat
                  </Heading>
                  <VStack align="start" spacing={3}>
                    <HStack align="start" spacing={3}>
                      <Box
                        w="2px"
                        h="100%"
                        bg="cyan.100"
                        borderRadius="full"
                        mt={1}
                      />
                      <Text>
                        Parteneriate pentru asigurarea{" "}
                        <Text as="span" fontWeight="bold">
                          relevanței programelor de studii
                        </Text>{" "}
                        în raport cu cerințele pieței.
                      </Text>
                    </HStack>
                    <HStack align="start" spacing={3}>
                      <Box
                        w="2px"
                        h="100%"
                        bg="cyan.100"
                        borderRadius="full"
                        mt={1}
                      />
                      <Text>
                        Dezvoltarea unui{" "}
                        <Text as="span" fontWeight="bold">
                          mediu educațional atractiv și eficient
                        </Text>
                        .
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              {/* Obiectiv 3 */}
              <GridItem colSpan={1}>
                <Box
                  p={6}
                  borderRadius="lg"
                  bg={cardBg}
                  boxShadow="xl"
                  border="1px solid"
                  borderColor="teal.100"
                  h="100%"
                  transition="all 0.3s"
                  _hover={{ transform: "translateY(-5px)", boxShadow: "2xl" }}
                >
                  <Heading
                    as="h3"
                    size="md"
                    mb={4}
                    color="teal.500"
                    display="flex"
                    alignItems="center"
                  >
                    <Box
                      w="30px"
                      h="30px"
                      borderRadius="full"
                      bg="teal.500"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mr={3}
                      fontSize="sm"
                      fontWeight="bold"
                    >
                      3
                    </Box>
                    Dezvoltarea competențelor profesionale ale elevilor
                  </Heading>
                  <VStack align="start" spacing={3}>
                    <HStack align="start" spacing={3}>
                      <Box
                        w="2px"
                        h="100%"
                        bg="teal.100"
                        borderRadius="full"
                        mt={1}
                      />
                      <Text>
                        Integrarea{" "}
                        <Text as="span" fontWeight="bold">
                          teoriei cu practica
                        </Text>{" "}
                        pentru o mai bună pregătire profesională.
                      </Text>
                    </HStack>
                    <HStack align="start" spacing={3}>
                      <Box
                        w="2px"
                        h="100%"
                        bg="teal.100"
                        borderRadius="full"
                        mt={1}
                      />
                      <Text>
                        Dobândirea de{" "}
                        <Text as="span" fontWeight="bold">
                          competențe esențiale
                        </Text>
                        : comunicare, gândire critică, rezolvare de probleme,
                        competențe digitale și verzi.
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              {/* Obiectiv 4 */}
              <GridItem colSpan={1}>
                <Box
                  p={6}
                  borderRadius="lg"
                  bg={cardBg}
                  boxShadow="xl"
                  border="1px solid"
                  borderColor="purple.100"
                  h="100%"
                  transition="all 0.3s"
                  _hover={{ transform: "translateY(-5px)", boxShadow: "2xl" }}
                >
                  <Heading
                    as="h3"
                    size="md"
                    mb={4}
                    color="purple.500"
                    display="flex"
                    alignItems="center"
                  >
                    <Box
                      w="30px"
                      h="30px"
                      borderRadius="full"
                      bg="purple.500"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mr={3}
                      fontSize="sm"
                      fontWeight="bold"
                    >
                      4
                    </Box>
                    Asigurarea incluziunii și echității în selecția elevilor
                  </Heading>
                  <VStack align="start" spacing={3}>
                    <HStack align="start" spacing={3}>
                      <Box
                        w="2px"
                        h="100%"
                        bg="purple.100"
                        borderRadius="full"
                        mt={1}
                      />
                      <Text>
                        Acces echitabil la stagii pentru elevii cu vârsta de{" "}
                        <Text as="span" fontWeight="bold">
                          maxim 29 de ani
                        </Text>{" "}
                        și domiciliul în Sud-Muntenia.
                      </Text>
                    </HStack>
                    <HStack align="start" spacing={3}>
                      <Box
                        w="2px"
                        h="100%"
                        bg="purple.100"
                        borderRadius="full"
                        mt={1}
                      />
                      <Text>
                        Respectarea principiilor de{" "}
                        <Text as="span" fontWeight="bold">
                          egalitate de șanse și non-discriminare
                        </Text>
                        .
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>
            </Grid>
          </Box>
        </ScaleFade>
      </Container>
    </Box>
  );
};

// Componentă pentru cercul cu icoană
const Circle = ({ color, icon }) => {
  return (
    <CircularProgress value={100} color={color} size="70px" thickness="4px">
      <CircularProgressLabel>
        <Icon as={icon} color={color} w={6} h={6} />
      </CircularProgressLabel>
    </CircularProgress>
  );
};

export default CREATEComponent;
